import { Layer } from './layer/layer';
import { GroupLayer } from './layer/groupLayer';
import { CurveLayer } from './layer/curveLayer';
import { LineLayer } from './layer/lineLayer';
import { PlainTextLayer } from './layer/plainTextLayer';
import { RasterImageLayer } from './layer/rasterImageLayer';
import { VectorImageLayer } from './layer/vectorImageLayer';
import { TableLayer } from './layer/tableLayer';
import { UndoRedoManager } from '@/utilityModels/undoRedoManager';

import * as def from '@/definitions';
import { Rectangle } from '@/utilityModels/rectangle';
import { ArrayHelper } from '@/helper/arrayHelper';
import { Vector2 } from '@/utilityModels/vector2';

import { EncryptionHelper } from '@/helper/encryptionHelper';
import { DebugHelper } from '@/helper/debugHelper';

/** １つの面(文字とか画像とかたくさんのShapeを持つことができる) */
export class Design
{
    // キャンバス幅
    private _width : number = 0;
    public get width() : number { return this._width; }
    public set width(width : number) { this._width = width; }

    // キャンバス高さ
    private _height : number = 0;
    public get height() : number { return this._height; }
    public set height(height : number) { this._height = height; }

    // キャンバス回転角
    private _rotate : number = 0;
    public get rotate() : number { return this._rotate; }
    public set rotate(rotate : number) { this._rotate = rotate; }

    // 親レイヤー
    private _layer : GroupLayer = new GroupLayer();
    public get layer() : GroupLayer { return this._layer; }
    public set layer(layer : GroupLayer) { this._layer = layer; }

    // 選択レイヤー
    private _selectLayers : Array<Layer> = new Array();
    public get selectLayers() { return this._selectLayers; }
    public addSelectLayer(layer)
    {
        this._selectLayers = ArrayHelper.add(this._selectLayers, layer);
    }
    public removeSelectLayer(layer)
    {
        this._selectLayers = ArrayHelper.remove(this._selectLayers, layer);
    }
    public clearSelectLayers()
    {
        this._selectLayers = new Array();
    }

    // 選択レイヤーをグループを外して取得する
    public getSelectLayerWithoutGroup() : Array<Layer>
    {
        var array = new Array();
        for(var i = 0; i < this.selectLayers.length; ++i)
        {
            var layer = this.selectLayers[i];
            if(layer instanceof GroupLayer)
            {
                // グループの場合は、子レイヤーを全部追加する
                var allChildLayers = layer.getAllChildLayers(false);
                for(var j = 0; j < allChildLayers.length; ++j)
                {
                    var targetLayer = allChildLayers[j];
                    // 追加時に追加済みだった場合は追加しない
                    var isContained = false;
                    for(var k = 0; k < array.length; ++k)
                    {
                        if(array[k] == targetLayer)
                        {
                            isContained = true;
                            break;
                        }
                    }
                    if(!isContained)
                    {
                        array.push(targetLayer);
                    }
                }
            }
            else
            {
                // 追加時に追加済みだった場合は追加しない
                var isContained = false;
                for(var k = 0; k < array.length; ++k)
                {
                    if(array[k] == layer)
                    {
                        isContained = true;
                        break;
                    }
                }
                if(!isContained)
                {
                    array.push(layer);
                }
            }
        }
        return array;
    }
    
    // カレントレイヤー
    public get currentLayer() : Layer | null 
    {
        // 選択レイヤーがなければnullを返す
        if(this.selectLayers.length == 0) { return null; }
        // 選択レイヤーの中で最も上にあるレイヤーを取得する
        return this.selectLayers[this.selectLayers.length - 1];
    }
    public set currentLayer(currentLayer : Layer | null)
    {
        // 選択レイヤーをクリア
        this.clearSelectLayers();

        if(currentLayer != null)
        {
            // 選択レイヤーを指定のレイヤーだけにする
            this.addSelectLayer(currentLayer);
        }
    }

    // 単位
    // 0: mm
    // 1: inch
    private _unit : number = 0;
    public get unit() : number { return this._unit; }
    public set unit(unit : number) { this._unit = unit; }

    // // サムネイル画像 base64
    // private _thumbnailImageBase64 : string = "";
    // public get thumbnailImageBase64() { return this._thumbnailImageBase64; }
    // public set thumbnailImageBase64(thumbnailImageBase64) { this._thumbnailImageBase64 = thumbnailImageBase64; }

    // // サムネイルsvg *画像作成したら消す
    // private _thumbnailSvg : string = "";
    // public get thumbnailSvg() { return this._thumbnailSvg; }
    // public set thumbnailSvg(thumbnailSvg) { this._thumbnailSvg = thumbnailSvg; }

    // // サムネイル画像幅
    // private _thumbnailImageWidth = 0;
    // public get thumbnailImageWidth() { return this._thumbnailImageWidth; }
    // public set thumbnailImageWidth(thumbnailImageWidth) { this._thumbnailImageWidth = thumbnailImageWidth; }

    // // サムネイル画像高さ
    // private _thumbnailImageHeight = 0;
    // public get thumbnailImageHeight() { return this._thumbnailImageHeight; }
    // public set thumbnailImageHeight(thumbnailImageHeight) { this._thumbnailImageHeight = thumbnailImageHeight; }

    // // サムネイル画像のsvgデータがオリジナルから何度回転しているか
    // private _thumbnailSvgRotate = 0;
    // public get thumbnailSvgRotate() { return this._thumbnailSvgRotate; }
    // public set thumbnailSvgRotate(thumbnailSvgRotate) { this._thumbnailSvgRotate = thumbnailSvgRotate; }

    // 複製
    public clone() : Design
    {
        var cloned = new Design();
        cloned.width = this.width;
        cloned.height = this.height;
        cloned.rotate = this.rotate;
        cloned.layer = this.layer.clone() as GroupLayer;
        cloned.unit = this.unit;
        // cloned.thumbnailImageBase64 = this._thumbnailImageBase64;
        // cloned.thumbnailSvg = this._thumbnailSvg;
        // cloned.thumbnailImageWidth = this._thumbnailImageWidth;
        // cloned.thumbnailImageHeight = this._thumbnailImageHeight;
        // cloned.thumbnailSvgRotate = this._thumbnailSvgRotate;

        return cloned;
    }

    // 全レイヤー取得
    // isGroupLayerContained: グループレイヤーを含めて取得する
    public getAllLayers(isGroupLayerContained = false) : Layer[]
    {
        var allLayers = this.layer.getAllChildLayers(isGroupLayerContained);
        // このレイヤーのもっとも親のレイヤーは含めてはいけないので外す
        var removeIndex = -1;
        for(var i = 0; i < allLayers.length; ++i)
        {
            if(allLayers[i] == this.layer)
            {
                removeIndex = i;
                break;
            }
        }
        if(removeIndex >= 0)
        {
            allLayers.splice(removeIndex, 1);
        }
        return allLayers;
    }

    // オブジェクトが存在するか
    // isGroupLayerContained: グループレイヤーを含めて取得する
    public hasAnyObject(isGroupLayerContained = true) : boolean
    {
        return this.getAllLayers(isGroupLayerContained).length > 0;
    }

    // レイヤーの親を取得
    public getParentLayer(layer : Layer) : GroupLayer | null
    {
        if(this.layer instanceof GroupLayer)
        {
            return this.getParent(this.layer, layer);
        }
        return null;
    }

    // 親レイヤーを再帰的に得る
    private getParent(start : GroupLayer, target : Layer) : GroupLayer | null
    {
        for(var i = 0; i < start.getLayerCount(); ++i)
        {
            var child = start.getLayer(i);
            if(child == target)
            {
                // 見つかった
                return start;
            }
            if(child instanceof GroupLayer)
            {
                // 子レイヤーを起点に探す
                var parent = this.getParent(child, target);
                if(parent != null)
                {
                    // 見つかったら返す
                    return parent;
                }
            }
        }
        // 見つからなかった
        return null;
    }

    // レイヤーをidから得る
    public getLayerFromId(id) : Layer | null
    {
        // 全レイヤー
        var allLayers = this.getAllLayers(true);
        for(var layer of allLayers)
        {
            if(layer.id == id)
            {
                return layer;
            }
        }
        return null;
    }

    // 与えられた座標にあるレイヤーを取得する
    // ignoreLock : ロック状態を無視する
    // isMobile : モバイルでの取得
    public getLayersFromPosition(x : number, y : number, ignoreLock : boolean = false, isMobile = false)
    {
        return this.getLayersFromPositionByGroup(this.layer, x, y, ignoreLock, isMobile);
    }

    private getLayersFromPositionByGroup(group : GroupLayer, x : number, y : number, ignoreLock : boolean = false, isMobile = false)
    {
        var getLayers : Array<Layer> = [];
        var layers = group.layers;
        for(var i = 0; i < layers.length; ++i)
        {
            var layer = layers[i];
            // レイヤーがロックされていたら無視する
            if(!ignoreLock && layer.isLocked) { continue; }
            if(layer instanceof GroupLayer)
            {
                // グループレイヤーについて追加
                var getLayersFromGroup = this.getLayersFromPositionByGroup(layer, x, y, ignoreLock);
                for(var j = 0; j < getLayersFromGroup.length; ++j)
                {
                    getLayers.push(getLayersFromGroup[j]);
                }
            }
            else
            {
                // 与えられた座標にレイヤーがあれば取得する
                if(isMobile && (layer.layerType == "line" || layer.layerType == "curve"))
                {
                    // モバイルで、直線、曲線は矩形範囲で判定する
                    if(layer.isRectContained(x - 0.1, y - 0.1, 0.2, 0.2)) { getLayers.push(layer); }
                }
                else
                {
                    if(layer.isPointContained(x, y)) { getLayers.push(layer); }
                }
            }
        }

        return getLayers;
    }

    // 与えられた範囲に含まれるレイヤーを返す
    // ignoreLock : ロック状態を無視する
    public getLayersRectContained(x : number, y : number, width : number, height : number, ignoreLock : boolean = false)
    {
        return this.getLayersRectContainedByGroup(this.layer, x, y, width, height, ignoreLock);
    }

    private getLayersRectContainedByGroup(group : GroupLayer, x : number, y : number, width : number, height : number, ignoreLock : boolean = false)
    {
        var getLayers : Array<Layer> = [];
        var layers = group.layers;
        for(var i = 0; i < layers.length; ++i)
        {
            var layer = layers[i];
            // レイヤーがロックされていたら無視する
            if(!ignoreLock && layer.isLocked) { continue; }
            if(layer instanceof GroupLayer)
            {
                // グループレイヤーについて追加
                var getLayersFromGroup = this.getLayersRectContainedByGroup(layer, x, y, width, height, ignoreLock);
                for(var j = 0; j < getLayersFromGroup.length; ++j)
                {
                    getLayers.push(getLayersFromGroup[j]);
                }
            }
            else
            {
                // 与えられた座標にレイヤーがあれば取得する
                if(layer.isRectContained(x, y, width, height)) { getLayers.push(layer); }
            }
        }

        return getLayers;
    }

    // 角度回転
    // rotateNumberLayer : NumberLayerを回転する
    public rotateDesign(delta : number, rotateNumberLayer = true)
    {
        // 計算の簡単のため、回転角を0以上、360未満に収める
        while(delta < 0) { delta += 360; }
        while(delta >= 360) { delta -= 360; }
        
        // 新しいデザインサイズ
        var newDesignWidth = (delta == 90 || delta == 270) ? this.height : this.width;
        var newDesignHeight = (delta == 90 || delta == 270) ? this.width : this.height;
        // 新しい回転角
        var newDesignRotate = this.rotate + delta;
        while(newDesignRotate < 0) { newDesignRotate += 360; }
        while(newDesignRotate >= 360) { newDesignRotate -= 360; }
        // グループレイヤー以外の全レイヤーについて再配置する
        for(var layer of this.getAllLayers(false))
        {
            // 連番レイヤー対応
            if(!rotateNumberLayer && layer.layerType == "number") { continue; }


            // 曲線レイヤーは通常の回転ができないので特殊処理
            if(layer instanceof CurveLayer)
            {
                layer.rotateFromDesign(delta, this.width / 2, this.height / 2, newDesignWidth / 2, newDesignHeight / 2);
                continue;
            }

            // レイヤーの回転角
            var newRotate = layer.rotate + delta;
            if(newRotate < -180) { newRotate += 360; }
            if(newRotate > 180) { newRotate -= 360; }

            // レイヤーの左上の点を回転させる。
            // 現在のレイヤーの左上の座標と、キャンバス中心との差分を求める
            var dx = layer.x - this.width / 2;
            var dy = layer.y - this.height / 2;
            // 新しい座標系でのキャンバス中心の座標
            var cx = newDesignWidth / 2;
            var cy = newDesignHeight / 2;
            // 新しい座標系での回転前の左上の座標
            var srcPosition = new Vector2(cx + dx, cy + dy);
            // この座標を回転する
            var position = srcPosition.rotateDegrees(delta, cx, cy);

            // レイヤーの値設定
            layer.rotate = newRotate;
            layer.x = position.x;
            layer.y = position.y;
        }

        // デザインサイズ更新
        this.width = newDesignWidth;
        this.height = newDesignHeight;

        // 回転
        this.rotate = newDesignRotate;
    }
    
    // アンドゥリドゥ管理クラス
    public undoRedoManager : UndoRedoManager = new UndoRedoManager();

    public static create(width : number, height : number) : Design
    {
        var design = new Design();
        design.width = width;
        design.height = height;
        design.layer = GroupLayer.create();

        // この時点でのhashを記録する
        design.updateHash();

        return design;
    }

    // JSON
    public toJSON()
    {
        // JSONパース用オブジェクト
        var cloneObj = {
            width : this.width,
            height : this.height,
            rotate : this.rotate,
            layer : this.layer,
            unit : this.unit,
            // thumbnailImageBase64 : this.thumbnailImageBase64,
            // thumbnailSvg : this._thumbnailSvg,
            // thumbnailImageWidth : this._thumbnailImageWidth,
            // thumbnailImageHeight : this._thumbnailImageHeight,
            // thumbnailSvgRotate : this._thumbnailSvgRotate
        };

        return cloneObj;
    }

    public static createFromJsonData(jsonData : any)
    {
        var design = new Design();
        if(jsonData.width) { design.width = jsonData.width; }
        if(jsonData.height) { design.height = jsonData.height; }
        if(jsonData.rotate) { design.rotate = jsonData.rotate; }
        if(jsonData.layer) { design.layer = this.createLayerFromJsonData(jsonData.layer); }
        if(jsonData.unit) { design.unit = jsonData.unit; }
        // if(jsonData.thumbnailImageBase64) { design.thumbnailImageBase64 = jsonData.thumbnailImageBase64; }
        // if(jsonData.thumbnailSvg) { design.thumbnailSvg = jsonData.thumbnailSvg; }
        // if(jsonData.thumbnailImageWidth) { design.thumbnailImageWidth = jsonData.thumbnailImageWidth; }
        // if(jsonData.thumbnailImageHeight) { design.thumbnailImageHeight = jsonData.thumbnailImageHeight; }
        // if(jsonData.thumbnailSvgRotate) { design.thumbnailSvgRotate = jsonData.thumbnailSvgRotate; }

        // この時点でのhashを記録する
        design.updateHash();

        return design;
    }

    public static createLayerFromJsonData(jsonData : any)
    {
        if(jsonData.layerType == "group")
        {
            return GroupLayer.createFromJsonData(jsonData);
        }

        return new GroupLayer();
    }

    // エラーチェック
    public hasError()
    {
        var error = false;
        var allLayers = this.getAllLayers(true);
        for(var i = 0; i < allLayers.length; ++i)
        {
            var layer = allLayers[i];
            if(layer.layerType == "convertError")
            {
                error = true;
                break;
            }
        }

        return error;
    } 

    // jsonに含めない
    // 前回開いた時 or 前回保存したときの内容のハッシュ
    public lastContentHash : string = "";

    // ハッシュをアップデートする
    public updateHash()
    {
        // openedFileNameはhashに含めない
        this.lastContentHash = this.calcHash();
        DebugHelper.trace("___last_hash : " + this.lastContentHash);
    }

    // 変更されたかどうか？
    public isModified()
    {
        var currentHash = this.calcHash();
        DebugHelper.trace("___last_hash : " + this.lastContentHash);
        DebugHelper.trace("current_hash : " + currentHash);
        return currentHash.toLowerCase() != this.lastContentHash.toLowerCase();
    }

    private calcHash()
    {
        // Hashに含めない情報をいったん逃してクリアする
        // var thumbnailImageBase64 = this.thumbnailImageBase64;
        // var thumbnailSvg = this.thumbnailSvg;
        // var thumbnailImageWidth = this.thumbnailImageWidth;
        // var thumbnailImageHeight = this.thumbnailImageHeight;
        // var thumbnailSvgRotate = this.thumbnailSvgRotate;
        // this.thumbnailImageBase64 = "";
        // this.thumbnailSvg = "";
        // this.thumbnailImageWidth = 0;
        // this.thumbnailImageHeight = 0;
        // this.thumbnailSvgRotate = 0;

        var hash = EncryptionHelper.calcHash(this);

        // 情報を元に戻す
        // this.thumbnailImageBase64 = thumbnailImageBase64;
        // this.thumbnailSvg = thumbnailSvg;
        // this.thumbnailImageWidth = thumbnailImageWidth;
        // this.thumbnailImageHeight = thumbnailImageHeight;
        // this.thumbnailSvgRotate = thumbnailSvgRotate;

        return hash;
    }
}